/**
 * Live preview of how the truck's freight splits (decision 2026-10-04, F2).
 * Display only — the saved split is the backend's
 * (transportService.syncTransportCostsInTransaction); nothing here is sent.
 *
 * INBOUND („Fabryka → klient” / „Fabryka → magazyn”): per carried ZD line —
 *   share, amount and the cost per 1 m² (else per unit of the line) in the
 *   truck currency and in PLN. The between-loads rule is one rate per unit of
 *   load, so splitting per line by the same key equals the server's
 *   ZD-then-line split up to rounding.
 * OUTBOUND („Magazyn → klient”): per ZK — share and the amount that lands in
 *   that order's costs (margin). Built by sharedTransportFormUtils
 *   buildAllocationPreview; this module only adds the PLN figure and the
 *   „było → jest” comparison.
 */
import type { TransportRouteDetailDto } from '../../dto/transport';
import {
  commercialAllocationMethodLabelKey,
  DEFAULT_ALLOCATION_METHOD,
  positionArea,
  positionPieces,
  positionVolume,
  roundMoney,
  type AllocationMethod,
} from '../../utils/transportCostAllocation';

export interface InboundPreviewLine {
  id: string;
  /** ZD id: the default key weighs the ZD loads first, then their lines. */
  groupId: string;
  /** ZD number. */
  orderLabel: string;
  name: string;
  /** Formatted product dimensions („gr. 18 x szer. 1250 x dł. 2500 mm”) — display only, never weighed. */
  dimensions?: string | null;
  quantity: number;
  unit: string;
  pcs?: number | null;
  m2?: number | null;
  m3?: number | null;
  width?: number | null;
  height?: number | null;
  length?: number | null;
  thickness?: number | null;
  countInPiecesOnly?: boolean;
  /** Goods value of the carried part in PLN; null = unknown. */
  valuePLN: number | null;
  /** Saved freight of the line (PLN, edit mode) for „było → jest”. */
  savedPLN?: number | null;
}

export interface CostSplitPreviewRow {
  key: string;
  orderLabel: string;
  name?: string;
  /** Display only: the line's formatted dimensions. */
  dimensions?: string | null;
  /** 0–100; null = this key cannot weigh the row. */
  sharePercent: number | null;
  /** Truck currency; null = unknown. */
  amount: number | null;
  amountPLN: number | null;
  /** Cost per 1 unit of the row (m² when known, else its own unit). */
  perUnit: { unit: string; amount: number; amountPLN: number | null } | null;
  valuePLN?: number | null;
  savedPLN?: number | null;
}

function lineWeight(line: InboundPreviewLine, method: AllocationMethod): number | null {
  const position = { ...line, source: 'ZD' as const, productName: line.name };
  switch (method) {
    case 'BY_SQM': return positionArea(position);
    case 'BY_CBM': return positionVolume(position);
    case 'BY_VALUE': return line.valuePLN !== null && line.valuePLN > 0 ? line.valuePLN : null;
    default: return null;
  }
}

/** PLN of an amount in the truck currency; null without a rate. */
export function toPLN(amount: number | null, rate: number | null): number | null {
  if (amount === null || rate === null || !Number.isFinite(rate) || rate <= 0) return null;
  return roundMoney(amount * rate);
}

// Default key (szt.) between the ZD loads — backend pickDefaultKeyBasisMetric:
// the first of m³, m², pieces that is > 0 for EVERY load; none → equal. m³ / m²
// are the lines' persisted (3-decimal) figures, as backend supplierLoadMetrics.
const DEFAULT_LOAD_METRICS: Array<(line: InboundPreviewLine) => number> = [
  (line) => positionVolume({ ...line, source: 'ZD', productName: line.name }),
  (line) => positionArea({ ...line, source: 'ZD', productName: line.name }),
  (line) => (String(line.unit || 'pcs') === 'pcs' ? Number(line.quantity) || 0 : 0),
];

function defaultKeyWeights(lines: InboundPreviewLine[]): number[] {
  const groups = Array.from(new Set(lines.map((line) => line.groupId)));
  const groupSum = (metric: (line: InboundPreviewLine) => number, groupId: string) => lines
    .filter((line) => line.groupId === groupId)
    .reduce((sum, line) => sum + metric(line), 0);
  const metric = DEFAULT_LOAD_METRICS.find((candidate) => groups.every((groupId) => groupSum(candidate, groupId) > 0));
  const groupWeight = new Map(groups.map((groupId) => [groupId, metric ? groupSum(metric, groupId) : 1]));
  const totalGroups = Array.from(groupWeight.values()).reduce((sum, weight) => sum + weight, 0);
  return lines.map((line) => {
    const siblings = lines.filter((candidate) => candidate.groupId === line.groupId);
    // Lines inside a ZD by pieces (szt. key), equal when none has pieces.
    const pieces = siblings.map((candidate) => positionPieces(candidate));
    const piecesTotal = pieces.reduce((sum, value) => sum + value, 0);
    const inGroup = piecesTotal > 0 ? positionPieces(line) / piecesTotal : 1 / siblings.length;
    return totalGroups > 0 ? ((groupWeight.get(line.groupId) || 0) / totalGroups) * inGroup : 0;
  });
}

/**
 * Per-line preview of an INBOUND truck. `transportCost` null/0 → shares only.
 * Amounts use the largest-remainder rounding so they add up to the cost.
 */
export function buildInboundCostSplitPreview({
  lines,
  method,
  transportCost,
  rateToPLN,
}: {
  lines: InboundPreviewLine[];
  method: AllocationMethod;
  transportCost: number | null;
  rateToPLN: number | null;
}): CostSplitPreviewRow[] {
  const weights = method === 'BY_COMMERCIAL_QTY'
    ? defaultKeyWeights(lines)
    : lines.map((line) => lineWeight(line, method));
  const total = weights.reduce<number>((sum, weight) => sum + (weight && weight > 0 ? weight : 0), 0);
  // A typed 0 is a price (every share 0,00); only an empty price is unknown („—”).
  const cost = transportCost !== null && Number.isFinite(transportCost) && transportCost >= 0 ? transportCost : null;
  const amounts = cost !== null && total > 0
    ? allocateLargestRemainder(cost, weights.map((weight) => (weight && weight > 0 ? weight : 0)))
    : lines.map(() => null);
  return lines.map((line, index) => {
    const weight = weights[index];
    const sharePercent = total > 0 && weight !== null ? Math.round((Math.max(weight, 0) / total) * 10000) / 100 : null;
    const amount = sharePercent === null ? null : amounts[index];
    const area = positionArea({ ...line, source: 'ZD', productName: line.name });
    const basis = area > 0 ? { unit: 'm2', quantity: area } : { unit: line.unit, quantity: Number(line.quantity) || 0 };
    // Round 3 (U21): rounded like the server (buildSupplierOrderTruckFreight):
    // PLN per unit = round(PLN amount / quantity), the truck currency per
    // unit = round(PLN per unit / rate) — never a rounded per-unit × rate.
    const amountPLN = toPLN(amount, rateToPLN);
    const perUnitPLN = amountPLN !== null && basis.quantity > 0 ? roundMoney(amountPLN / basis.quantity) : null;
    const perUnitAmount = amount === null || !(basis.quantity > 0)
      ? null
      : (perUnitPLN !== null && rateToPLN ? roundMoney(perUnitPLN / rateToPLN) : roundMoney(amount / basis.quantity));
    return {
      key: line.id,
      orderLabel: line.orderLabel,
      name: line.name,
      dimensions: line.dimensions ?? null,
      sharePercent,
      amount,
      amountPLN,
      perUnit: perUnitAmount === null ? null : {
        unit: basis.unit,
        amount: perUnitAmount,
        amountPLN: perUnitPLN,
      },
      valuePLN: line.valuePLN,
      savedPLN: line.savedPLN ?? null,
    };
  });
}

/** Cumulative largest-remainder split to the grosz (sums to `total`). */
export function allocateLargestRemainder(total: number, weights: number[]): number[] {
  const sum = weights.reduce((acc, weight) => acc + weight, 0);
  if (!(sum > 0)) return weights.map(() => 0);
  const cents = Math.round(total * 100);
  const raw = weights.map((weight) => (cents * weight) / sum);
  const floors = raw.map(Math.floor);
  let rest = cents - floors.reduce((acc, value) => acc + value, 0);
  const order = raw
    .map((value, index) => ({ index, fraction: value - Math.floor(value) }))
    .sort((a, b) => b.fraction - a.fraction || a.index - b.index);
  for (const { index } of order) {
    if (rest <= 0) break;
    floors[index] += 1;
    rest -= 1;
  }
  return floors.map((value) => value / 100);
}

/** „Suma kwot zgadza się z kosztem auta” check of the manual key (0.01 tolerance). */
export function manualSplitDifference(manualAmounts: Array<number | null | undefined>, transportCost: number | null): number | null {
  if (transportCost === null || !Number.isFinite(transportCost)) return null;
  const sum = manualAmounts.reduce<number>((acc, value) => acc + (Number(value) || 0), 0);
  const difference = roundMoney(sum - transportCost);
  return Math.abs(difference) < 0.01 ? 0 : difference;
}

/**
 * The saved FX snapshot the next save keeps — mirror of the server rule
 * (transportService.syncTransportCostsInTransaction, review PR #267): only
 * while the currency and `transportRate` are unchanged (a legacy truck with
 * an empty `transportRate` counts as changed once a rate is typed, exactly
 * like hasChangedTransportRate); the ZD stock share snapshot first, else the
 * ZK cost rows, each only in the truck's currency. null = today's NBP rate.
 */
export function resolveSavedPreviewRate(
  transport: Pick<TransportRouteDetailDto, 'currency' | 'transportRate' | 'supplierOrders' | 'routeOrders'> | null | undefined,
  currency: string | undefined,
  transportRate: number | null | undefined,
): { rate: number; effectiveDate: string | null; saved: true } | null {
  if (!transport || !currency || currency === 'PLN' || currency !== transport.currency) return null;
  const saved = transport.transportRate ?? null;
  const typed = transportRate ?? null;
  if (saved === null || typed === null || Math.abs(Number(typed) - Number(saved)) >= 0.005) return null;
  const share = (transport.supplierOrders || [])
    .map((row) => row.freightShare)
    .find((row) => row && row.currency === currency && Number(row.exchangeRate) > 0);
  if (share?.exchangeRate) return { rate: share.exchangeRate, effectiveDate: null, saved: true };
  const cost = (transport.routeOrders || [])
    .find((row) => row.originalCurrency === currency && Number(row.exchangeRateUsed) > 0);
  return cost?.exchangeRateUsed
    ? { rate: cost.exchangeRateUsed, effectiveDate: cost.exchangeRateEffectiveDate ?? null, saved: true }
    : null;
}

export type DefaultKeyMeasure = 'volume' | 'area' | 'pieces' | 'equal';

type MeasurePosition = Pick<InboundPreviewLine, 'quantity' | 'unit' | 'm2' | 'm3' | 'width' | 'height' | 'length' | 'thickness' | 'countInPiecesOnly'> & { name?: string | null };

/**
 * Which measure the default key (szt.) really weighs on this truck — mirror of
 * backend pickDefaultKeyBasisMetric: the first of m³, m², pieces that is > 0
 * for EVERY load (ZK or ZD); none → every load weighs the same (weight is not
 * known on the form). Usability round 2026-10-05: the picker names it.
 */
export function resolveDefaultKeyMeasure(loads: MeasurePosition[][]): DefaultKeyMeasure {
  if (loads.length === 0) return 'pieces';
  const measures: Array<[DefaultKeyMeasure, (position: MeasurePosition) => number]> = [
    ['volume', (position) => positionVolume({ ...position, source: 'ZD', productName: position.name })],
    ['area', (position) => positionArea({ ...position, source: 'ZD', productName: position.name })],
    ['pieces', (position) => (String(position.unit || 'pcs') === 'pcs' ? Number(position.quantity) || 0 : 0)],
  ];
  const found = measures.find(([, measure]) => loads.every((load) => load.reduce((sum, position) => sum + measure(position), 0) > 0));
  return found ? found[0] : 'equal';
}

/**
 * The loads of a saved truck for resolveDefaultKeyMeasure (detail page, U1
 * round 2): an INBOUND truck's ZD by what it carries, an OUTBOUND truck's ZK
 * by their cargo lines.
 */
export function defaultKeyLoadsOfRoute(
  route: Pick<TransportRouteDetailDto, 'direction' | 'supplierOrders' | 'cargoItems'>,
): MeasurePosition[][] {
  if (route.direction === 'INBOUND') {
    return (route.supplierOrders || []).map((supplierOrder) => (supplierOrder.loadItems || [])
      .filter((item) => item.carriedSource !== 'NONE')
      .map((item) => {
        const set = item.carried ?? item;
        return {
          name: item.name,
          quantity: Number(item.carried?.quantity ?? item.quantity) || 0,
          unit: String(item.carried?.unit ?? item.unit ?? 'pcs'),
          m2: set.quantityM2 ?? null,
          m3: set.quantityM3 ?? null,
          width: item.width,
          height: item.height,
          length: item.length,
          thickness: item.thickness,
          countInPiecesOnly: item.countInPiecesOnly === true,
        };
      }))
      .filter((load) => load.length > 0);
  }
  const byOrder = new Map<string, MeasurePosition[]>();
  (route.cargoItems || []).forEach((item) => {
    const load = byOrder.get(item.orderId) || [];
    load.push({
      name: item.productName,
      quantity: Number(item.quantity) || 0,
      unit: String(item.unit || 'pcs'),
      m2: item.quantityM2 ?? null,
      m3: item.quantityM3 ?? null,
      width: item.width ?? null,
      height: item.height ?? null,
      length: item.length ?? null,
      thickness: item.thickness ?? null,
    });
    byOrder.set(item.orderId, load);
  });
  return Array.from(byOrder.values());
}

/**
 * Name of the default key as it really works (owner 2026-10-06). OUTBOUND:
 * the one measure of the whole truck. INBOUND: between ZD loads that measure,
 * inside a ZD the lines' common unit (pieces when the units differ) — with a
 * single ZD only the inside split matters, so „według objętości” would mislead.
 * One home for the truck form, the key bar and the cost tile.
 */
export function defaultKeyLabel(
  loads: MeasurePosition[][],
  inbound: boolean,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  const between = resolveDefaultKeyMeasure(loads);
  if (!inbound) return t(`transport:allocationKey.defaultMeasure.${between}`);
  const units = Array.from(new Set(loads.flat().map((position) => String(position.unit || 'pcs'))));
  const inside = units.length === 1 && ['m2', 'm3'].includes(units[0]) ? units[0] : 'pcs';
  const insideName = t(`transport:allocationKey.unitName.${inside}`);
  if (loads.length <= 1) return t('transport:allocationKey.defaultInbound.single', { inside: insideName });
  // Nothing weighs the ZD against each other (no m³, m², szt.): they share it equally.
  return between === 'equal'
    ? t('transport:allocationKey.defaultInbound.manyEqual', { inside: insideName })
    : t('transport:allocationKey.defaultInbound.many', { between: t(`transport:allocationKey.measureName.${between}`), inside: insideName });
}

/**
 * The key a saved truck splits its cost by, as the operator reads it: the
 * default key names the measure it really uses (round 2, U1). One home for
 * the truck page and the „Dołóż” success message (round 3, B2).
 */
export function routeAllocationKeyLabel(
  route: Pick<TransportRouteDetailDto, 'direction' | 'supplierOrders' | 'cargoItems' | 'costAllocationMethod'>,
  t: (key: string, options?: Record<string, unknown>) => string,
): string {
  return route.costAllocationMethod === DEFAULT_ALLOCATION_METHOD
    ? defaultKeyLabel(defaultKeyLoadsOfRoute(route), route.direction === 'INBOUND', t)
    : t(`transport:${commercialAllocationMethodLabelKey(route.costAllocationMethod as AllocationMethod)}`);
}

/** True when a saved amount and the preview differ by at least a grosz. */
export function hasChanged(savedPLN: number | null | undefined, nextPLN: number | null): boolean {
  if (savedPLN === null || savedPLN === undefined || nextPLN === null) return false;
  return Math.abs(roundMoney(savedPLN) - roundMoney(nextPLN)) >= 0.01;
}
